import {
  alertIcon,
  checkIcon,
  pauseIcon,
  questionIcon,
  stopIcon
} from '../icons';

export type MedicationAdherence =
  | 'taking-as-directed'
  | 'taking-not-as-directed'
  | 'on-hold-as-directed'
  | 'on-hold-not-as-directed'
  | 'stopped-as-directed'
  | 'stopped-not-as-directed'
  | 'unknown';

export const medicationAdherences: {
  id: MedicationAdherence;
  title: string;
}[] = [
  {
    id: 'taking-as-directed',
    title: 'Taking as directed'
  },
  {
    id: 'taking-not-as-directed',
    title: 'Not taking as directed'
  },
  {
    id: 'on-hold-as-directed',
    title: 'On hold per instructions'
  },
  {
    id: 'on-hold-not-as-directed',
    title: 'On hold against instructions'
  },
  {
    id: 'stopped-as-directed',
    title: 'Stopped as prescribed'
  },
  {
    id: 'stopped-not-as-directed',
    title: 'Stopped against instructions'
  },
  {
    id: 'unknown',
    title: 'Unknown'
  }
];

export const medicationAdherenceToPrimerStatus: Record<
  MedicationAdherence,
  null | 'attention' | 'danger'
> = {
  'taking-as-directed': null,
  'taking-not-as-directed': 'danger',
  'on-hold-as-directed': null,
  'on-hold-not-as-directed': 'danger',
  'stopped-as-directed': null,
  'stopped-not-as-directed': 'danger',
  unknown: 'attention'
};

export const medicationAdherenceIcons: Record<MedicationAdherence, string> = {
  'taking-as-directed': checkIcon,
  'taking-not-as-directed': alertIcon,
  'on-hold-as-directed': pauseIcon,
  'on-hold-not-as-directed': alertIcon,
  'stopped-as-directed': stopIcon,
  'stopped-not-as-directed': alertIcon,
  unknown: questionIcon
};

export const maxActivityMedications = 40;

export interface ActivityMedication {
  activity_medication_id: string; // uniqueidentifier
  // activity_id: string; // uniqueidentifier
  property_id: number; // int

  // DrugBank medication data
  drugbank_pcid: string; // varchar(16)
  drugbank_level: number; // tinyint
  display_name: string; // varchar(450)
  name: string; // varchar(300)
  brand_name?: string | null; // varchar(50), nullable
  strength: string | null; // varchar(100), nullable
  route: string | null; // varchar(50), nullable
  form: string | null; // varchar(100), nullable
  rxcui: number | null; // int, nullable

  // Medication record / statement elements
  is_new: boolean; // bit
  dose_and_frequency: string; // varchar(100)
  start_date: string | null; // date, nullable
  end_date: string | null; // date, nullable
  prescriber: string; // varchar(50)
  notes: string; // nvarchar(300)
  adherence: MedicationAdherence; // varchar(24)

  // update_user_id: string; // uniqueidentifier
  // update_organization_id: string; // uniqueidentifier
  // update_datetime: string; // datetime2(7)
}

export type MedicationInfo = Pick<
  ActivityMedication,
  | 'drugbank_pcid'
  | 'drugbank_level'
  | 'display_name'
  | 'name'
  | 'brand_name'
  | 'strength'
  | 'route'
  | 'form'
  | 'rxcui'
>;

export function drugApiPath({
  organization_id,
  person_id,
  activity_id,
  medication
}: {
  organization_id: string;
  person_id: string | null;
  activity_id: string | null;
  medication: MedicationInfo | null;
}) {
  /*
  /api/medications/autocomplete/:drugbank_pcid/:mode(strengths|routes|forms)
  */
  const base = `/api/organizations/${organization_id}/medications/autocomplete`;
  const search = new URLSearchParams();
  if (person_id) {
    search.set('person_id', person_id);
  }
  if (activity_id) {
    search.set('activity_id', activity_id);
  }
  const {
    drugbank_level: level,
    drugbank_pcid: pcid,
    strength,
    route,
    form
  } = medication ?? {};
  if (!pcid || (strength && route && form)) {
    return `${base}?${search}`;
  }
  if (!strength) {
    return `${base}/${pcid}/strengths?${search}`;
  }
  if (!route) {
    return `${base}/${pcid}/routes?${search}`;
  }
  if (!form) {
    return `${base}/${pcid}/forms?${search}`;
  }
  throw new Error(`Invalid drugbank_level: ${level}`);
}
